import { useEffect, useState } from "react";
import { cn } from "./cn.js";

export function hostFromUrl(url: string): string {
	try {
		return new URL(url).hostname.replace(/^www\./i, "").toLowerCase();
	} catch {
		return url
			.replace(/^https?:\/\//i, "")
			.replace(/\/.*$/, "")
			.replace(/^www\./i, "")
			.toLowerCase();
	}
}

function faviconSources(host: string): string[] {
	return [
		`https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=128`,
		`https://icons.duckduckgo.com/ip3/${encodeURIComponent(host)}.ico`,
	];
}

type Props = {
	url: string;
	className?: string;
	/** Pulse ring while a job is in flight. */
	live?: boolean;
};

/**
 * Client brand mark for pack / clone cards. Google then DuckDuckGo,
 * then a letter monogram — same sources as the marketing login gate.
 */
export function SiteFavicon({ url, className, live = false }: Props) {
	const host = hostFromUrl(url);
	const letter = (host[0] ?? "?").toUpperCase();
	const [src, setSrc] = useState<string | null>(null);

	useEffect(() => {
		if (!host.includes(".")) {
			setSrc(null);
			return;
		}
		const sources = faviconSources(host);
		let cancelled = false;
		let index = 0;

		const tryNext = () => {
			const next = sources[index];
			index += 1;
			if (!next || cancelled) {
				if (!cancelled) setSrc(null);
				return;
			}
			const img = new Image();
			img.referrerPolicy = "no-referrer";
			img.onload = () => {
				if (cancelled) return;
				// Google’s unknown-domain globe is 16×16.
				if (img.naturalWidth < 24 || img.naturalHeight < 24) {
					tryNext();
					return;
				}
				setSrc(next);
			};
			img.onerror = () => {
				if (!cancelled) tryNext();
			};
			img.src = next;
		};

		setSrc(null);
		tryNext();
		return () => {
			cancelled = true;
		};
	}, [host]);

	return (
		<span
			className={cn(
				"mc-site-favicon",
				live && "mc-site-favicon--live",
				className,
			)}
			aria-hidden
		>
			{src ? (
				<img src={src} alt="" referrerPolicy="no-referrer" />
			) : (
				<span className="mc-site-favicon-mono">{letter}</span>
			)}
		</span>
	);
}
